/**
 * QR Code modo byte, correção M (ISO/IEC 18004).
 * Reimplementação própria para o Pix. A matriz é conferida contra o pacote qrcode
 * em strings forçadas para byte mode antes de remover essa dependência.
 */

const CODEWORDS = [
  0, 26, 44, 70, 100, 134, 172, 196, 242, 292, 346,
  404, 466, 532, 581, 655, 733, 815, 901, 991, 1085,
  1156, 1258, 1364, 1474, 1588, 1706, 1828, 1921, 2051, 2185,
  2323, 2465, 2611, 2761, 2876, 3034, 3196, 3362, 3532, 3706
];

const ECC_BLOCKS_M = [
  1, 1, 1, 2, 2, 4, 4, 4, 5, 5,
  5, 8, 9, 9, 10, 10, 11, 13, 14, 16,
  17, 17, 18, 20, 21, 23, 25, 26, 28, 29,
  31, 33, 35, 37, 38, 40, 43, 45, 47, 49
];

const ECC_TOTAL_M = [
  10, 16, 26, 36, 48, 64, 72, 88, 110, 130,
  150, 176, 198, 216, 240, 280, 308, 338, 364, 416,
  442, 476, 504, 560, 588, 644, 700, 728, 784, 812,
  868, 924, 980, 1036, 1064, 1120, 1204, 1260, 1316, 1372
];

const G15 = (1 << 10) | (1 << 8) | (1 << 5) | (1 << 4) | (1 << 2) | (1 << 1) | (1 << 0);
const G15_MASK = (1 << 14) | (1 << 12) | (1 << 10) | (1 << 4) | (1 << 1);
const G18 = (1 << 12) | (1 << 11) | (1 << 10) | (1 << 9) | (1 << 8) | (1 << 5) | (1 << 2) | (1 << 0);
const ECC_M_BIT = 0;

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

(function initGalois() {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a] + LOG[b]];
}

function polyMul(p1: number[], p2: number[]): number[] {
  const coeff = new Array<number>(p1.length + p2.length - 1).fill(0);
  for (let i = 0; i < p1.length; i++) {
    for (let j = 0; j < p2.length; j++) {
      coeff[i + j] ^= gfMul(p1[i], p2[j]);
    }
  }
  return coeff;
}

function polyMod(dividend: number[], divisor: number[]): number[] {
  let result = dividend.slice();
  while (result.length - divisor.length >= 0) {
    const coeff = result[0];
    for (let i = 0; i < divisor.length; i++) {
      result[i] ^= gfMul(divisor[i], coeff);
    }
    let offset = 0;
    while (offset < result.length && result[offset] === 0) offset++;
    result = result.slice(offset);
  }
  return result;
}

function generator(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i++) {
    poly = polyMul(poly, [1, EXP[i]]);
  }
  return poly;
}

function reedSolomon(data: Uint8Array, degree: number): Uint8Array {
  const gen = generator(degree);
  const padded = new Array<number>(data.length + degree).fill(0);
  for (let i = 0; i < data.length; i++) padded[i] = data[i];
  const remainder = polyMod(padded, gen);
  const out = new Uint8Array(degree);
  const start = degree - remainder.length;
  for (let i = 0; i < remainder.length; i++) out[start + i] = remainder[i];
  return out;
}

function bchDigit(data: number): number {
  let digit = 0;
  let value = data >>> 0;
  while (value !== 0) {
    digit++;
    value >>>= 1;
  }
  return digit;
}

function formatBits(mask: number): number {
  const data = (ECC_M_BIT << 3) | mask;
  let d = data << 10;
  const g15Digit = bchDigit(G15);
  while (bchDigit(d) - g15Digit >= 0) {
    d ^= G15 << (bchDigit(d) - g15Digit);
  }
  return ((data << 10) | d) ^ G15_MASK;
}

function versionBits(version: number): number {
  let d = version << 12;
  const g18Digit = bchDigit(G18);
  while (bchDigit(d) - g18Digit >= 0) {
    d ^= G18 << (bchDigit(d) - g18Digit);
  }
  return (version << 12) | d;
}

function symbolSize(version: number): number {
  return version * 4 + 17;
}

function charCountBits(version: number): number {
  if (version < 10) return 8;
  if (version < 27) return 16;
  return 16;
}

function byteCapacity(version: number): number {
  const dataBits = (CODEWORDS[version] - ECC_TOTAL_M[version - 1]) * 8;
  return Math.floor((dataBits - (4 + charCountBits(version))) / 8);
}

function bestVersion(byteLength: number): number {
  for (let version = 1; version <= 40; version++) {
    if (byteLength <= byteCapacity(version)) return version;
  }
  throw new Error('Texto longo demais para um QR Code');
}

class BitBuffer {
  private bits: number[] = [];

  put(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i--) {
      this.bits.push((value >>> i) & 1);
    }
  }

  putBit(bit: number): void {
    this.bits.push(bit & 1);
  }

  get length(): number {
    return this.bits.length;
  }

  toBytes(): Uint8Array {
    const out = new Uint8Array(Math.ceil(this.bits.length / 8));
    for (let i = 0; i < this.bits.length; i++) {
      if (this.bits[i]) out[i >> 3] |= 128 >> (i & 7);
    }
    return out;
  }
}

function createCodewords(bytes: Uint8Array, version: number): Uint8Array {
  const totalCodewords = CODEWORDS[version];
  const ecTotal = ECC_TOTAL_M[version - 1];
  const dataTotal = totalCodewords - ecTotal;
  const blocks = ECC_BLOCKS_M[version - 1];
  const blocksInGroup2 = totalCodewords % blocks;
  const blocksInGroup1 = blocks - blocksInGroup2;
  const dataInGroup1 = Math.floor(dataTotal / blocks);
  const dataInGroup2 = dataInGroup1 + 1;
  const ecCount = Math.floor(totalCodewords / blocks) - dataInGroup1;

  const dc: Uint8Array[] = [];
  const ec: Uint8Array[] = [];
  let offset = 0;
  let maxData = 0;
  for (let b = 0; b < blocks; b++) {
    const dataSize = b < blocksInGroup1 ? dataInGroup1 : dataInGroup2;
    const chunk = bytes.slice(offset, offset + dataSize);
    dc.push(chunk);
    ec.push(reedSolomon(chunk, ecCount));
    offset += dataSize;
    if (dataSize > maxData) maxData = dataSize;
  }

  const data = new Uint8Array(totalCodewords);
  let index = 0;
  for (let i = 0; i < maxData; i++) {
    for (let r = 0; r < blocks; r++) {
      if (i < dc[r].length) data[index++] = dc[r][i];
    }
  }
  for (let i = 0; i < ecCount; i++) {
    for (let r = 0; r < blocks; r++) data[index++] = ec[r][i];
  }
  return data;
}

function encodeData(payload: Uint8Array, version: number): Uint8Array {
  const buffer = new BitBuffer();
  buffer.put(1 << 2, 4);
  buffer.put(payload.length, charCountBits(version));
  for (let i = 0; i < payload.length; i++) buffer.put(payload[i], 8);

  const totalBits = (CODEWORDS[version] - ECC_TOTAL_M[version - 1]) * 8;
  if (buffer.length + 4 <= totalBits) buffer.put(0, 4);
  while (buffer.length % 8 !== 0) buffer.putBit(0);
  const remaining = (totalBits - buffer.length) / 8;
  for (let i = 0; i < remaining; i++) buffer.put(i % 2 ? 0x11 : 0xec, 8);
  return createCodewords(buffer.toBytes(), version);
}

interface Matrix {
  size: number;
  data: Uint8Array;
  reserved: Uint8Array;
}

function makeMatrix(size: number): Matrix {
  return {
    size,
    data: new Uint8Array(size * size),
    reserved: new Uint8Array(size * size)
  };
}

function setModule(matrix: Matrix, row: number, col: number, value: boolean | number, reserved = false): void {
  const index = row * matrix.size + col;
  matrix.data[index] = value ? 1 : 0;
  if (reserved) matrix.reserved[index] = 1;
}

function getModule(matrix: Matrix, row: number, col: number): number {
  return matrix.data[row * matrix.size + col];
}

function isReserved(matrix: Matrix, row: number, col: number): boolean {
  return matrix.reserved[row * matrix.size + col] === 1;
}

function xorModule(matrix: Matrix, row: number, col: number, value: boolean): void {
  matrix.data[row * matrix.size + col] ^= value ? 1 : 0;
}

function setupFinder(matrix: Matrix): void {
  const size = matrix.size;
  const positions: Array<[number, number]> = [
    [0, 0],
    [size - 7, 0],
    [0, size - 7]
  ];
  for (const [row, col] of positions) {
    for (let r = -1; r <= 7; r++) {
      if (row + r <= -1 || size <= row + r) continue;
      for (let c = -1; c <= 7; c++) {
        if (col + c <= -1 || size <= col + c) continue;
        const dark =
          (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
          (c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
          (r >= 2 && r <= 4 && c >= 2 && c <= 4);
        setModule(matrix, row + r, col + c, dark, true);
      }
    }
  }
}

function setupTiming(matrix: Matrix): void {
  for (let r = 8; r < matrix.size - 8; r++) {
    const value = r % 2 === 0;
    setModule(matrix, r, 6, value, true);
    setModule(matrix, 6, r, value, true);
  }
}

function alignmentCoords(version: number): number[] {
  if (version === 1) return [];
  const size = symbolSize(version);
  const posCount = Math.floor(version / 7) + 2;
  const intervals = size === 145 ? 26 : Math.ceil((size - 13) / (2 * posCount - 2)) * 2;
  const positions = [size - 7];
  for (let i = 1; i < posCount - 1; i++) positions[i] = positions[i - 1] - intervals;
  positions.push(6);
  return positions.reverse();
}

function setupAlignment(matrix: Matrix, version: number): void {
  const pos = alignmentCoords(version);
  const posLength = pos.length;
  for (let i = 0; i < posLength; i++) {
    for (let j = 0; j < posLength; j++) {
      if ((i === 0 && j === 0) || (i === 0 && j === posLength - 1) || (i === posLength - 1 && j === 0)) {
        continue;
      }
      const row = pos[i];
      const col = pos[j];
      for (let r = -2; r <= 2; r++) {
        for (let c = -2; c <= 2; c++) {
          const dark = r === -2 || r === 2 || c === -2 || c === 2 || (r === 0 && c === 0);
          setModule(matrix, row + r, col + c, dark, true);
        }
      }
    }
  }
}

function setupVersion(matrix: Matrix, version: number): void {
  const bits = versionBits(version);
  const size = matrix.size;
  for (let i = 0; i < 18; i++) {
    const row = Math.floor(i / 3);
    const col = (i % 3) + size - 8 - 3;
    const dark = ((bits >> i) & 1) === 1;
    setModule(matrix, row, col, dark, true);
    setModule(matrix, col, row, dark, true);
  }
}

function setupFormat(matrix: Matrix, mask: number): void {
  const bits = formatBits(mask);
  const size = matrix.size;
  for (let i = 0; i < 15; i++) {
    const dark = ((bits >> i) & 1) === 1;
    if (i < 6) setModule(matrix, i, 8, dark, true);
    else if (i < 8) setModule(matrix, i + 1, 8, dark, true);
    else setModule(matrix, size - 15 + i, 8, dark, true);

    if (i < 8) setModule(matrix, 8, size - i - 1, dark, true);
    else if (i < 9) setModule(matrix, 8, 15 - i - 1 + 1, dark, true);
    else setModule(matrix, 8, 15 - i - 1, dark, true);
  }
  setModule(matrix, size - 8, 8, 1, true);
}

function setupData(matrix: Matrix, data: Uint8Array): void {
  const size = matrix.size;
  let inc = -1;
  let row = size - 1;
  let bitIndex = 7;
  let byteIndex = 0;

  for (let col = size - 1; col > 0; col -= 2) {
    if (col === 6) col--;
    while (true) {
      for (let c = 0; c < 2; c++) {
        if (!isReserved(matrix, row, col - c)) {
          let dark = false;
          if (byteIndex < data.length) dark = ((data[byteIndex] >>> bitIndex) & 1) === 1;
          setModule(matrix, row, col - c, dark);
          bitIndex--;
          if (bitIndex === -1) {
            byteIndex++;
            bitIndex = 7;
          }
        }
      }
      row += inc;
      if (row < 0 || size <= row) {
        row -= inc;
        inc = -inc;
        break;
      }
    }
  }
}

function maskAt(pattern: number, row: number, col: number): boolean {
  switch (pattern) {
    case 0: return (row + col) % 2 === 0;
    case 1: return row % 2 === 0;
    case 2: return col % 3 === 0;
    case 3: return (row + col) % 3 === 0;
    case 4: return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0;
    case 5: return ((row * col) % 2) + ((row * col) % 3) === 0;
    case 6: return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0;
    case 7: return (((row * col) % 3) + ((row + col) % 2)) % 2 === 0;
    default: throw new Error('Máscara inválida');
  }
}

function applyMask(pattern: number, matrix: Matrix): void {
  for (let col = 0; col < matrix.size; col++) {
    for (let row = 0; row < matrix.size; row++) {
      if (isReserved(matrix, row, col)) continue;
      xorModule(matrix, row, col, maskAt(pattern, row, col));
    }
  }
}

function penalty(matrix: Matrix): number {
  const size = matrix.size;
  let points = 0;
  for (let row = 0; row < size; row++) {
    let sameCol = 0;
    let sameRow = 0;
    let lastCol: number | null = null;
    let lastRow: number | null = null;
    for (let col = 0; col < size; col++) {
      let module = getModule(matrix, row, col);
      if (module === lastCol) sameCol++;
      else {
        if (sameCol >= 5) points += 3 + (sameCol - 5);
        lastCol = module;
        sameCol = 1;
      }
      module = getModule(matrix, col, row);
      if (module === lastRow) sameRow++;
      else {
        if (sameRow >= 5) points += 3 + (sameRow - 5);
        lastRow = module;
        sameRow = 1;
      }
    }
    if (sameCol >= 5) points += 3 + (sameCol - 5);
    if (sameRow >= 5) points += 3 + (sameRow - 5);
  }

  let blocks = 0;
  for (let row = 0; row < size - 1; row++) {
    for (let col = 0; col < size - 1; col++) {
      const sum = getModule(matrix, row, col) + getModule(matrix, row, col + 1) +
        getModule(matrix, row + 1, col) + getModule(matrix, row + 1, col + 1);
      if (sum === 4 || sum === 0) blocks++;
    }
  }
  points += blocks * 3;

  let finderLike = 0;
  for (let row = 0; row < size; row++) {
    let bitsCol = 0;
    let bitsRow = 0;
    for (let col = 0; col < size; col++) {
      bitsCol = ((bitsCol << 1) & 0x7ff) | getModule(matrix, row, col);
      if (col >= 10 && (bitsCol === 0x5d0 || bitsCol === 0x05d)) finderLike++;
      bitsRow = ((bitsRow << 1) & 0x7ff) | getModule(matrix, col, row);
      if (col >= 10 && (bitsRow === 0x5d0 || bitsRow === 0x05d)) finderLike++;
    }
  }
  points += finderLike * 40;

  let dark = 0;
  for (let i = 0; i < matrix.data.length; i++) dark += matrix.data[i];
  const k = Math.abs(Math.ceil((dark * 100 / matrix.data.length) / 5) - 10);
  points += k * 10;
  return points;
}

function bestMask(matrix: Matrix): number {
  let best = 0;
  let lower = Infinity;
  for (let pattern = 0; pattern < 8; pattern++) {
    setupFormat(matrix, pattern);
    applyMask(pattern, matrix);
    const score = penalty(matrix);
    applyMask(pattern, matrix);
    if (score < lower) {
      lower = score;
      best = pattern;
    }
  }
  return best;
}

export interface QrSymbol {
  size: number;
  modules: Uint8Array;
}

export function encodeQrModules(text: string): QrSymbol {
  if (!text) throw new Error('Texto vazio para o QR Code');
  const payload = new TextEncoder().encode(text);
  const version = bestVersion(payload.length);
  const codewords = encodeData(payload, version);
  const matrix = makeMatrix(symbolSize(version));
  setupFinder(matrix);
  setupTiming(matrix);
  setupAlignment(matrix, version);
  setupFormat(matrix, 0);
  if (version >= 7) setupVersion(matrix, version);
  setupData(matrix, codewords);
  const mask = bestMask(matrix);
  applyMask(mask, matrix);
  setupFormat(matrix, mask);
  return { size: matrix.size, modules: matrix.data };
}

export interface QrSvgOptions {
  margin?: number;
  dark?: string;
  light?: string;
  width?: number;
}

export function renderQrSvg(text: string, options: QrSvgOptions = {}): string {
  const margin = options.margin ?? 1;
  const dark = options.dark ?? '#0f172a';
  const light = options.light ?? '#ffffff';
  const symbol = encodeQrModules(text);
  const size = symbol.size + margin * 2;
  const widthAttr = options.width ? ` width="${options.width}" height="${options.width}"` : '';
  let rects = '';
  for (let row = 0; row < symbol.size; row++) {
    for (let col = 0; col < symbol.size; col++) {
      if (symbol.modules[row * symbol.size + col]) {
        rects += `<rect x="${col + margin}" y="${row + margin}" width="1" height="1"/>`;
      }
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg"${widthAttr} viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges"><rect width="${size}" height="${size}" fill="${light}"/><g fill="${dark}">${rects}</g></svg>`;
}
